import { computed, inject, Injectable, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ZegoExpressEngine } from 'zego-express-engine-webrtc';
import type { ZegoStreamList } from 'zego-express-engine-webrtc/sdk/code/zh/ZegoExpressEntity.web';
import ZegoLocalStream from 'zego-express-engine-webrtc/sdk/code/zh/ZegoLocalStream.web';

import { ZegoEngineService } from '../../../services/zego-engine.service';
import { ZegoMediaControlService } from '../../../services/zego-media-control.service';
import { ZegoStreamService } from '../../../services/zego-stream.service';
import { createLogger } from '../../../utils/logger.util';
import { CALL_STATE, CALL_TYPE } from '../constants';
import { CallType } from '../types/call';
import { CallSessionStoreService } from './call-session-store.service';

type RoomStreamUpdateCallBack = (
  roomID: string,
  updateType: 'DELETE' | 'ADD',
  streamList: ZegoStreamList[],
  extendedData: string,
) => void;

@Injectable({
  providedIn: 'root',
})
export class CallZegoService {
  private readonly logger = createLogger('Call - CallZegoService');

  private zegoEngine: ZegoEngineService = inject(ZegoEngineService);
  private zegoStreamService = inject(ZegoStreamService);
  private zegoMediaControlService = inject(ZegoMediaControlService);
  private toastr = inject(ToastrService);
  private callSessionStore = inject(CallSessionStoreService);

  engine: ZegoExpressEngine;

  hasUserInRoom = signal<boolean>(false);

  private readonly _remoteStreams = signal<Record<string, MediaStream>>({});
  remoteStreams = computed(() => this._remoteStreams());

  private readonly _remoteSharingStreamIds = signal<string[]>([]);
  isRemoteSharing = computed(() => this._remoteSharingStreamIds().length > 0);

  private roomStreamDeleteTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private onStreamDeleteTimeout: (() => void) | null = null;

  setStreamDeleteTimeoutHandler(handler: () => void): void {
    this.onStreamDeleteTimeout = handler;
  }

  constructor() {
    this.engine = this.zegoEngine.engine;
  }

  getEngine(): ZegoExpressEngine {
    return this.engine;
  }

  async loginRoom(roomID: string, userID: string, userName: string): Promise<boolean> {
    return await this.zegoEngine.loginRoom(roomID, userID, userName);
  }

  logoutRoom(roomID: string): void {
    this.zegoEngine.logoutRoom(roomID);
    this._remoteStreams.set({});
    this._remoteSharingStreamIds.set([]);
    this.hasUserInRoom.set(false);
    if (this.roomStreamDeleteTimeoutId) {
      clearTimeout(this.roomStreamDeleteTimeoutId);
      this.roomStreamDeleteTimeoutId = null;
    }
  }

  async createAndPublishLocalStream(
    userID: string,
    isCameraEnabled: boolean,
    isMicEnabled: boolean,
    type: CallType,
  ): Promise<{ localStream: ZegoLocalStream; streamID: string } | null> {
    const streamResult = await this.zegoStreamService.createAndPublishStream(
      userID,
      isCameraEnabled,
      isMicEnabled,
      false,
      type === CALL_TYPE.AUDIO ? CALL_TYPE.VIDEO : 'none',
    );
    return streamResult;
  }

  stopPubishAndDestroyStream(stream: ZegoLocalStream | null, streamId: string) {
    if (!stream) {
      return;
    }
    try {
      this.zegoStreamService.stopPublishingStream(streamId, stream);
    } catch (error) {
      this.logger.error('Clear local stream error:', error);
    }
  }

  private onRoomStreamUpdateHandler: RoomStreamUpdateCallBack | null = null;

  setupStreamCallbacks(): void {
    if (this.onRoomStreamUpdateHandler) return;

    this.onRoomStreamUpdateHandler = async (
      roomID: string,
      updateType: 'ADD' | 'DELETE',
      streamList: ZegoStreamList[],
      extendedData: string,
    ) => {
      this.logger.log('Room Stream Update:', {
        roomID,
        updateType,
        streamList,
        extendedData,
      });
      const mainStreams = streamList.filter((s) => !s.streamID.includes('screen'));

      if (updateType === 'ADD') {
        if (this.roomStreamDeleteTimeoutId) {
          clearTimeout(this.roomStreamDeleteTimeoutId);
          this.roomStreamDeleteTimeoutId = null;
        }

        if (this.callSessionStore.callState() === CALL_STATE.CONNECTING && mainStreams.length > 0) {
          const now = Date.now();
          this.callSessionStore.setCallStartTime(now);
          this.callSessionStore.setCurrentCallState(CALL_STATE.CONNECTED);
          this.callSessionStore.setPendingInvitation(null);
        }

        streamList.forEach(async (streamInfo) => {
          if (this.hasUserInRoom() === false) {
            this.hasUserInRoom.set(true);
          }
          const streamID = streamInfo.streamID;
          const isScreenStream = streamID.includes('screen');

          if (isScreenStream) {
            this._remoteSharingStreamIds.update((ids) => Array.from(new Set([...ids, streamID])));
            this.handleRemoteScreenSharing(streamID, updateType);
          }

          const remoteStream = await this.zegoStreamService.playStreamToDOM(
            streamID,
            isScreenStream ? 'remote-screen' : 'remote-stream',
          );

          if (remoteStream) {
            this._remoteStreams.update((rs) => {
              return { ...rs, [streamID]: remoteStream };
            });
          }
        });
      } else if (updateType === 'DELETE') {
        for (const streamInfo of streamList) {
          this._remoteStreams.update((rs) => {
            const newRs = { ...rs };
            delete newRs[streamInfo.streamID];
            return newRs;
          });
          this.engine.stopPlayingStream(streamInfo.streamID);
          if (streamInfo.streamID.includes('screen')) {
            this._remoteSharingStreamIds.update((ids) =>
              ids.filter((id) => id !== streamInfo.streamID),
            );
            this.handleRemoteScreenSharing(streamInfo.streamID, updateType);
          }
        }

        if (
          mainStreams.length > 0 &&
          (this.callSessionStore.callState() === CALL_STATE.CONNECTED ||
            this.callSessionStore.callState() === CALL_STATE.CONNECTING) &&
          !this.roomStreamDeleteTimeoutId
        ) {
          this.roomStreamDeleteTimeoutId = setTimeout(() => {
            this.onStreamDeleteTimeout?.();
          }, 20000);
        }
      }
    };
    this.engine.on('roomStreamUpdate', this.onRoomStreamUpdateHandler);
  }

  handleRemoteScreenSharing(streamID: string, updateType: 'ADD' | 'DELETE') {
    const userID = streamID.replace('_screen', '');
    if (updateType === 'ADD') {
      if (this.zegoMediaControlService.mediaState().isScreenSharing) {
        this.zegoMediaControlService.stopScreenShare();
      }
      this.toastr.info(
        `${this.callSessionStore.getNameParticipantByUserID(userID)} vừa chia sẻ màn hình.`,
      );
    } else {
      this.toastr.info(
        `${this.callSessionStore.getNameParticipantByUserID(userID)} đã dừng chia sẻ màn hình.`,
      );
    }
  }

  clearStreamCallbacks(): void {
    if (this.onRoomStreamUpdateHandler) {
      this.engine.off('roomStreamUpdate', this.onRoomStreamUpdateHandler);
      this.onRoomStreamUpdateHandler = null;
    }
    if (this.roomStreamDeleteTimeoutId) {
      clearTimeout(this.roomStreamDeleteTimeoutId);
      this.roomStreamDeleteTimeoutId = null;
    }
  }
}
